// ═══════════════════════════════════════════════════════════════════════════════
// src/components/WorkspaceDock.jsx
// Persistent workspace sidebar — shows saved research contexts across all modules.
// ═══════════════════════════════════════════════════════════════════════════════
//
// USAGE in App.jsx:
//
//   import WorkspaceDock from './components/WorkspaceDock';
//
//   // In render, alongside the sidebar:
//   <WorkspaceDock
//     isAuthenticated={isAuthenticated}
//     onRestore={(ws) => {
//       setActiveModule(ws.module_id);
//       // pass ws context down to the module via a ref or context
//     }}
//     onAuthOpen={openAuth}
//   />
//
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

// ── Design tokens (matches existing platform palette) ─────────────────────────
const C = {
  void:    '#020508',
  deep:    '#060b14',
  surface: '#0a1120',
  b0:      '#0f1a2e',
  b1:      '#1e293b',
  b2:      '#334155',
  t1:      '#e2e8f0',
  t2:      '#b6c3d8',
  t3:      '#8396b3',
  t4:      '#627795',
  cyan:    '#22d3ee',
  violet:  '#a78bfa',
  green:   '#4ade80',
  gold:    '#fbbf24',
  red:     '#f87171',
  orange:  '#fb923c',
  gcyan:   'rgba(34,211,238,0.08)',
  gviolet: 'rgba(167,139,250,0.08)',
  ggreen:  'rgba(74,222,128,0.08)',
  ggold:   'rgba(251,191,36,0.08)',
};

// ── Module config ─────────────────────────────────────────────────────────────
const MODULE_META = {
  auto_eda:          { label: 'Auto EDA',           icon: '📊', color: C.cyan   },
  doc_analysis:      { label: 'Document Analysis',  icon: '📄', color: C.violet },
  image_insights:    { label: 'Image Insights',     icon: '🖼️', color: '#f472b6' },
  speech_studio:     { label: 'Speech Studio',      icon: '🎙️', color: C.gold   },
  video_intelligence:{ label: 'Video Intelligence', icon: '🎞️', color: C.orange },
  ml_experiments:    { label: 'ML Experiments',     icon: '⚡', color: C.orange },
  dl_experiments:    { label: 'DL Experiments',     icon: '🧠', color: C.green },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function summaryLine(summary, moduleId) {
  if (!summary) return '';
  const parts = [];
  if (summary.filename)   parts.push(summary.filename);
  if (summary.rows)       parts.push(`${summary.rows.toLocaleString()} rows`);
  if (summary.cols)       parts.push(`${summary.cols} cols`);
  if (summary.word_count) parts.push(`${summary.word_count.toLocaleString()} words`);
  if (summary.duration_s) parts.push(`${Math.round(summary.duration_s)}s audio`);
  if (summary.pages)      parts.push(`${summary.pages} pages`);
  if (summary.best_score != null) {
    const score = Number(summary.best_score);
    parts.push(`best: ${score > 1 ? score.toFixed(1) : (score * 100).toFixed(1)}%`);
  }
  return parts.join(' · ');
}

function versionLabel(versionNo) {
  return `V${versionNo}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────
function WorkspaceCard({ ws, onRestore, onDelete, deleting }) {
  const mod   = MODULE_META[ws.module_id] || { label: ws.module_id, icon: '◈', color: C.t3 };
  const sub   = summaryLine(ws.summary, ws.module_id);
  const note  = ws.summary?.findings || ws.summary?.note || '';
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const versions = Array.isArray(ws.versions) ? [...ws.versions].sort((a, b) => (b.version_no || 0) - (a.version_no || 0)) : [];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setConfirmDelete(false); }}
      style={{
        position:     'relative',
        border:       `1px solid ${hovered ? mod.color + '44' : C.b1}`,
        borderRadius: '4px',
        padding:      '10px 12px',
        background:   hovered ? mod.color + '08' : C.surface,
        cursor:       'pointer',
        transition:   'all 0.15s',
        marginBottom: '6px',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ color: mod.color, fontSize: 11 }}>{mod.icon}</span>
        <span style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize:   9,
          color:      mod.color,
          letterSpacing: '.08em',
          fontWeight: 700,
          textTransform: 'uppercase',
        }}>{mod.label}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.t4 }}>
          {relativeTime(ws.updated_at)}
        </span>
      </div>

      {/* Label */}
      <div style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize:   11,
        color:      C.t1,
        fontWeight: 600,
        marginBottom: 3,
        overflow:   'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {ws.label}
      </div>

      {/* Summary line */}
      {sub && (
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: C.t4, marginBottom: note ? 3 : 0 }}>
          {sub}
        </div>
      )}

      {/* Findings note */}
      {note && (
        <div style={{
          fontFamily:   'JetBrains Mono, monospace',
          fontSize:     9,
          color:        C.t3,
          overflow:     'hidden',
          display:      '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight:   1.5,
        }}>
          {note}
        </div>
      )}

      {/* Tab badge */}
      {ws.active_tab && (
        <div style={{ marginTop: 6 }}>
          <span style={{
            fontFamily:  'JetBrains Mono, monospace',
            fontSize:    8,
            color:       mod.color,
            background:  mod.color + '18',
            border:      `1px solid ${mod.color}33`,
            borderRadius: '2px',
            padding:     '1px 5px',
            letterSpacing: '.06em',
          }}>
            {ws.active_tab}
          </span>
          <span style={{
            marginLeft: 6,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 8,
            color: C.t3,
          }}>
            {versionLabel(ws.current_version || ws.version_count || 1)} · {ws.version_count || 1} versions
          </span>
        </div>
      )}

      {versions.length > 1 && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded((prev) => !prev); }}
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 8,
              color: C.t4,
              background: 'transparent',
              border: `1px solid ${C.b2}`,
              borderRadius: '2px',
              padding: '2px 6px',
              cursor: 'pointer',
              letterSpacing: '.06em',
            }}
          >
            {expanded ? 'HIDE VERSIONS' : 'SHOW VERSIONS'}
          </button>
        </div>
      )}

      {expanded && versions.length > 0 && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${C.b1}`, paddingTop: 8, display: 'grid', gap: 6 }}>
          {versions.map((version) => {
            const versionSub = summaryLine(version.summary, ws.module_id);
            return (
              <div key={`${ws.id}_${version.version_no}`} style={{ border: `1px solid ${C.b1}`, borderRadius: 3, padding: '7px 8px', background: C.deep }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: mod.color, fontWeight: 700 }}>
                    {versionLabel(version.version_no || 1)}
                  </span>
                  {version.parent_version ? (
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.t4 }}>
                      from {versionLabel(version.parent_version)}
                    </span>
                  ) : (
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.t4 }}>
                      root
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.t4 }}>
                    {relativeTime(version.created_at)}
                  </span>
                </div>
                {versionSub && (
                  <div style={{ marginTop: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 8.5, color: C.t4 }}>
                    {versionSub}
                  </div>
                )}
                <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestore({ ...ws, active_tab: version.active_tab || ws.active_tab, version_no: version.version_no });
                    }}
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 8,
                      color: mod.color,
                      background: mod.color + '18',
                      border: `1px solid ${mod.color}44`,
                      borderRadius: '2px',
                      padding: '3px 7px',
                      cursor: 'pointer',
                      letterSpacing: '.06em',
                      fontWeight: 700,
                    }}
                  >RESUME {versionLabel(version.version_no || 1)} →</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action buttons (hover only) */}
      {hovered && (
        <div style={{
          position:   'absolute',
          bottom:     8,
          right:      8,
          display:    'flex',
          gap:        4,
        }}>
          {!confirmDelete ? (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onRestore({ ...ws, version_no: ws.current_version || ws.version_count || 1 }); }}
                style={{
                  fontFamily:  'JetBrains Mono, monospace',
                  fontSize:    8,
                  color:       mod.color,
                  background:  mod.color + '18',
                  border:      `1px solid ${mod.color}44`,
                  borderRadius: '2px',
                  padding:     '3px 7px',
                  cursor:      'pointer',
                  letterSpacing: '.06em',
                  fontWeight:  700,
                }}
              >RESUME →</button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
                style={{
                  fontFamily:  'JetBrains Mono, monospace',
                  fontSize:    8,
                  color:       C.t4,
                  background:  'transparent',
                  border:      `1px solid ${C.b2}`,
                  borderRadius: '2px',
                  padding:     '3px 7px',
                  cursor:      'pointer',
                }}
              >✕</button>
            </>
          ) : (
            <>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.red, lineHeight: '22px' }}>
                Delete?
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(ws.id); setConfirmDelete(false); }}
                disabled={deleting}
                style={{
                  fontFamily:  'JetBrains Mono, monospace',
                  fontSize:    8,
                  color:       C.red,
                  background:  'rgba(248,113,113,0.12)',
                  border:      `1px solid ${C.red}44`,
                  borderRadius: '2px',
                  padding:     '3px 7px',
                  cursor:      'pointer',
                }}
              >YES</button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                style={{
                  fontFamily:  'JetBrains Mono, monospace',
                  fontSize:    8,
                  color:       C.t4,
                  background:  'transparent',
                  border:      `1px solid ${C.b2}`,
                  borderRadius: '2px',
                  padding:     '3px 7px',
                  cursor:      'pointer',
                }}
              >NO</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main WorkspaceDock ────────────────────────────────────────────────────────
export default function WorkspaceDock({ isAuthenticated, onRestore, onAuthOpen, isPremium }) {
  const [open,       setOpen]       = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [deleting,   setDeleting]   = useState(null); // id being deleted
  const [cap,        setCap]        = useState(3);
  const [pulse,      setPulse]      = useState(false); // flash on new save
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await api.workspace.list();
      setWorkspaces(res.workspaces || []);
      setCap(res.cap || 3);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Load on open
  useEffect(() => { if (open) load(); }, [open, load]);

  // Listen for workspace-saved events from modules
  useEffect(() => {
    const handler = () => {
      setPulse(true);
      setTimeout(() => setPulse(false), 1200);
      if (open) load();
    };
    window.addEventListener('dd-workspace-saved', handler);
    return () => window.removeEventListener('dd-workspace-saved', handler);
  }, [open, load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.workspace.remove(id);
      setWorkspaces(prev => prev.filter(w => w.id !== id));
    } catch (e) {
      // silent
    } finally {
      setDeleting(null);
    }
  };

  const handleRestore = (ws) => {
    setOpen(false);
    onRestore(ws);
  };

  const count = workspaces.length;

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        onClick={() => {
          if (!isAuthenticated) { onAuthOpen?.(); return; }
          setOpen(prev => !prev);
        }}
        title="Workspaces"
        style={{
          display:      'flex',
          alignItems:   'center',
          gap:          5,
          padding:      '5px 10px',
          background:   open ? C.gcyan : 'transparent',
          border:       `1px solid ${open ? C.cyan + '44' : C.b1}`,
          borderRadius: '3px',
          color:        open ? C.cyan : C.t3,
          fontFamily:   'JetBrains Mono, monospace',
          fontSize:      9,
          letterSpacing: '.08em',
          fontWeight:    600,
          cursor:        'pointer',
          transition:    'all 0.15s',
          position:      'relative',
        }}
      >
        <span style={{ fontSize: 11 }}>⬡</span>
        <span>MY WORKSPACE</span>
        {count > 0 && (
          <span style={{
            background:   pulse ? C.cyan : C.b1,
            color:        pulse ? C.void : C.t3,
            borderRadius: '2px',
            padding:      '0 4px',
            fontSize:      8,
            fontWeight:    700,
            transition:    'all 0.3s',
            minWidth:      14,
            textAlign:     'center',
          }}>{count}</span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position:     'absolute',
          top:          'calc(100% + 6px)',
          right:        0,
          width:        320,
          maxHeight:    520,
          background:   C.deep,
          border:       `1px solid ${C.b1}`,
          borderRadius: '4px',
          boxShadow:    '0 8px 32px rgba(0,0,0,0.6)',
          zIndex:       9999,
          display:      'flex',
          flexDirection:'column',
          overflow:     'hidden',
        }}>
          {/* Panel header */}
          <div style={{
            padding:      '10px 12px',
            borderBottom: `1px solid ${C.b1}`,
            display:      'flex',
            alignItems:   'center',
            gap:          8,
            flexShrink:   0,
          }}>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: C.cyan, fontWeight: 700, letterSpacing: '.1em' }}>
              WORKSPACES
            </span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 8, color: C.t4 }}>
              {count}/{cap}
            </span>
            {!isPremium && (
              <span style={{
                marginLeft:   'auto',
                fontFamily:   'JetBrains Mono, monospace',
                fontSize:     8,
                color:        C.gold,
                background:   'rgba(251,191,36,0.1)',
                border:       `1px solid ${C.gold}33`,
                borderRadius: '2px',
                padding:      '1px 5px',
              }}>
                FREE: {count}/{cap} — upgrade for 50
              </span>
            )}
            <button
              onClick={load}
              style={{ marginLeft: isPremium ? 'auto' : 0, background: 'transparent', border: 'none', color: C.t4, cursor: 'pointer', fontSize: 11, padding: 2 }}
              title="Refresh"
            >↻</button>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
            {loading && (
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: C.t4, textAlign: 'center', padding: '20px 0' }}>
                loading...
              </div>
            )}
            {!loading && workspaces.length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⬡</div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: C.t4, lineHeight: 1.7 }}>
                  No saved workspaces yet.<br />
                  Run a module and click<br />
                  <span style={{ color: C.cyan }}>Save Version</span> to resume later.
                </div>
              </div>
            )}
            {!loading && workspaces.map(ws => (
              <WorkspaceCard
                key={ws.id}
                ws={ws}
                onRestore={handleRestore}
                onDelete={handleDelete}
                deleting={deleting === ws.id}
              />
            ))}
          </div>

          {/* Footer */}
          <div style={{
            padding:    '8px 12px',
            borderTop:  `1px solid ${C.b1}`,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize:    8,
            color:       C.t4,
            flexShrink:  0,
          }}>
            Raw uploaded data stays local. Saved versions store research context and restore snapshots.
          </div>
        </div>
      )}
    </div>
  );
}
